/* global HTMLDivElement */

import { makeStyles, tokens } from "@fluentui/react-components";
import * as React from "react";
import { Query } from "@/types";
import { queryToCardData } from "@/utils/card-utils";
import Card from "../../cards";

const { useRef } = React;

type Props = {
  questionItems: Query[];
};

export default function QuestionsList({ questionItems }: Props) {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new items are added
  React.useEffect(() => {
    containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight });
  }, [questionItems]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.content}>
        {questionItems.map((item) => (
          <Card
            key={item.id}
            data={queryToCardData(item)}
            variant="default"
          />
        ))}
      </div>
    </div>
  );
}

const useStyles = makeStyles({
  container: {
    flex: 1,
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.4)",
    },
  },

  content: {
    display: "grid",
    gap: "16px",
    borderRadius: "16px",
  },

  question_item__container: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "start",
    backgroundColor: tokens.colorNeutralBackground2,
    paddingLeft: "24px",
    paddingRight: "24px",
    paddingTop: "16px",
    paddingBottom: "16px",
    borderRadius: "16px",
  },

  heading_container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },

  heading_text__container: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  heading_text: {
    fontSize: "12px",
    fontWeight: "bold",
  },

  link_icon: {
    color: "#0078d4",
  },

  badge_container: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },

  no_match_badge: {
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "8px",
    paddingRight: "8px",
    backgroundColor: "#666",
    borderRadius: "16px",
    fontSize: "10px",
    color: "white",
  },

  question_item__title: {
    paddingTop: "8px",
    fontSize: "12px",
    fontWeight: "bold",
  },

  question_item__question: {
    fontSize: "12px",
    fontStyle: "italic",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    wordBreak: "break-word",
  },

  question_item__answer: {
    fontSize: "12px",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    wordBreak: "break-word",
  },

  divider: {
    height: "48px",
  },

  button_container: {
    display: "flex",
    gap: "4px",
    justifyContent: "end",
    paddingTop: "8px",
  },

  button: {
    color: "#fff",
    backgroundColor: tokens.colorPaletteLavenderForeground2,
    ["&:hover"]: {
      backgroundColor: tokens.colorPaletteLavenderBackground2,
    },
    fontSize: "12px",
  },

  button__content: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },

  table: {
    borderCollapse: "collapse",
    width: "100%",
    borderSpacing: "0",
  },

  th: {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: "#f4f4f4",
    fontWeight: "bold",
    fontSize: "12px",
  },

  td: {
    border: "1px solid #ddd",
    padding: "8px",
    fontSize: "12px",
  },
});
